<script setup lang="ts">
interface Props {
  type?: 'button' | 'submit' | 'reset';
  variant?: 'primary' | 'secondary' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  loading?: boolean;
  loadingLabel?: string;
}

const props = withDefaults(defineProps<Props>(), {
  type: 'button',
  variant: 'primary',
  size: 'md',
  disabled: false,
  loading: false,
  loadingLabel: 'Loading...',
});

const variantClasses = {
  primary: 'bg-sw-blue-light/80 hover:bg-sw-blue-light/70 text-white',
  secondary: 'bg-sw-dark-2 hover:bg-sw-dark-2/80 text-white',
  outline: 'bg-transparent border border-sw-purple text-sw-purple hover:bg-sw-purple/10',
};

const sizeClasses = {
  sm: 'py-2 px-4 text-sm',
  md: 'py-3 px-6 text-base',
  lg: 'py-[17px] px-8 text-base',
};
</script>

<template>
  <button
    :type="type"
    :disabled="disabled || loading"
    :class="[
      'w-full font-semibold rounded border-none transition-colors duration-200',
      variantClasses[variant],
      sizeClasses[size],
      { 'opacity-65 cursor-not-allowed': disabled || loading, 'cursor-pointer': !disabled && !loading },
    ]"
  >
    <span v-if="loading" class="inline-flex items-center justify-center">
      <svg class="animate-spin mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path
          class="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        ></path>
      </svg>
      {{ loadingLabel }}
    </span>
    <slot v-else />
  </button>
</template>
